import { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import Icons from "@/assets";
import { getApiErrorMessage } from "@/utils/helper";

const StyledContainer = styled(Container)(({ theme }) => ({
  backgroundColor: "#F0EAFF",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "5vw",
  [theme.breakpoints.up("md")]: {
    height: "100dvh",
    minHeight: "auto",
    paddingTop: "4%",
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    display: "block",
    overflowY: "scroll",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: "8vw",
  marginTop: "14vw",
  [theme.breakpoints.up("md")]: {
    marginBottom: "2vw",
    marginTop: 0,
  },
}));

const StyledButton = styled(Button)(() => ({
  fontWeight: 700,
  fontSize: "4vw",
  lineHeight: "130%",
  letterSpacing: "0%",
  textAlign: "center",
  textTransform: "none",
  borderRadius: "6vw",
  padding: "3vw 8vw",
  marginTop: "6vw",
  backgroundImage: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
  color: "#fff",
  width: "80%",
  "&:hover": {
    backgroundImage: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
    boxShadow: "0 1vw 3vw rgba(157, 121, 244, 0.4)",
  },
  "&.Mui-disabled": {
    background: "#ccc",
    color: "#999",
  },
  "@media (min-width: 600px)": {
    fontSize: "1.5vw",
    borderRadius: "2vw",
    padding: "1vw 3vw",
    marginTop: "2vw",
  },
}));

const StyledTextField = styled(TextField)(() => ({
  marginBottom: "4vw",
  backgroundColor: "rgba(255, 255, 255, 1)",
  borderRadius: "6vw",
  border: "0.25vw solid rgba(217, 217, 217, 1)",
  "& .MuiOutlinedInput-root": {
    borderRadius: "6vw",
    "& fieldset": {
      borderColor: "rgba(217, 217, 217, 1)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(217, 217, 217, 1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(217, 217, 217, 1)",
    },
  },
  "& .MuiInputBase-input": {
    padding: "3vw 4vw",
    fontSize: "3.5vw",
  },
  "@media (min-width: 600px)": {
    marginBottom: "1.5vw",
    borderRadius: "2vw",
    border: "0.1vw solid rgba(217, 217, 217, 1)",
    "& .MuiOutlinedInput-root": {
      borderRadius: "2vw",
    },
    "& .MuiInputBase-input": {
      padding: "1vw 1.5vw",
      fontSize: "1.2vw",
    },
  },
  "@media (min-width: 900px)": {
    marginBottom: "1.5vw",
    borderRadius: "2vw",
    border: "0.1vw solid rgba(217, 217, 217, 1)",
    "& .MuiOutlinedInput-root": {
      borderRadius: "2vw",
    },
    "& .MuiInputBase-input": {
      padding: "1vw 1.5vw",
      fontSize: "1.2vw",
    },
  },
}));

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [role, setRole] = useState("contributor");
  const [isCooldown, setIsCooldown] = useState(false);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { api } = useApiClient();

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleReset = async () => {
    if (isCooldown || isLoading) return;

    if (!token) {
      toast.error("Invalid or missing reset token.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    const pwdRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
    if (!pwdRegex.test(password)) {
      toast.error("Password must be at least 8 characters, with 1 uppercase, 1 number, and 1 special character.");
      return;
    }

    setIsLoading(true);
    setIsCooldown(true);

    try {
      const response = await api.post(ENDPOINTS.AUTH.RESET_PASSWORD, {
        token,
        newPassword: password,
      });
      if (response?.data) {
        setIsSuccess(true);
        if (response.data.data?.role) {
          setRole(response.data.data.role);
        }
        toast.success(response.data.message || "Password reset successful!");
      }
    } catch (error: unknown) {
      toast.error(
        getApiErrorMessage(error, "Failed to reset password. Token might be expired.")
      );
    } finally {
      setIsLoading(false);
      debounceTimeoutRef.current = setTimeout(() => {
        setIsCooldown(false);
      }, 5000);
    }
  };

  const renderPasswordAdornment = (
    isVisible: boolean,
    toggleVisibility: () => void
  ) => (
    <InputAdornment position="end">
      <IconButton onClick={toggleVisibility} edge="end" disabled={isLoading}>
        <Box
          component={"img"}
          src={isVisible ? Icons.eyeoff : Icons.eyeon}
          sx={{
            height: { xs: "5.83vw", sm: "1.5vw", md: "1.67vw" },
            width: { xs: "5.83vw", sm: "1.5vw", md: "1.67vw" },
          }}
        />
      </IconButton>
    </InputAdornment>
  );

  return (
    <StyledContainer maxWidth={false}>
      <LogoContainer>
        <Box
          component="img"
          src={Icons.WheelChair}
          alt="Wheelchair"
          sx={{
            width: { xs: "64.81vw", sm: "16vw", md: "0" },
            height: { xs: "64.81vw", sm: "16vw", md: "0" },
            marginBottom: "4vw",
            display: { md: "none" }
          }}
        />
        <Box
          component="img"
          src={Icons.Logo}
          alt="logo"
          sx={{
            width: { xs: "42vw", md: "30.42vw" },
            height: { xs: "13vw", md: "9.53vw" },
            maxWidth: { xs: "160px", md: "none" },
            maxHeight: { xs: "50px", md: "none" }
          }}
        />
      </LogoContainer>

      <Box sx={{ 
        width: { xs: "100%", md: "53.4vw" }, 
        maxWidth: { xs: "400px", md: "none" }, 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center",
        mx: { md: "auto" }
      }}>
        {isSuccess ? (
          <>
            <Typography
              sx={{
                fontSize: { xs: "5vw", sm: "1.8vw" },
                fontWeight: 600,
                color: "#1a1a1a",
                textAlign: "center",
                mb: "4vw",
              }}
            >
              Password Reset Successful
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "3.5vw", sm: "1.2vw" },
                color: "#666",
                textAlign: "center",
                mb: "6vw",
              }}
            >
              Your password has been successfully updated. You can now login with your new password.
            </Typography>
            {role !== "user" && (
              <StyledButton
                onClick={() => {
                  if (role === "moderator") {
                    window.location.href = "/moderate/";
                  } else {
                    navigate("/");
                  }
                }}
                variant="contained"
              >
                Go to {role === "moderator" ? "Moderator Login" : "Login"}
              </StyledButton>
            )}
          </>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: { xs: "5vw", sm: "1.8vw" },
                fontWeight: 500,
                color: "#1a1a1a",
                textAlign: "center",
                mb: { xs: "6vw", md: "3vw"},
              }}
            >
              Create New Password
            </Typography>
            
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontSize: { xs: "3.5vw", sm: "1.2vw", md: "0.97vw" },
                  color: "#000000",
                  mb: { xs: "2vw", md: "1.4%" },
                  ml: { xs: "2vw", md: "2%" },
                }}
              >
                New Password
              </Typography>
              <StyledTextField
                fullWidth
                type={showPassword ? "text" : "password"}
                variant="outlined"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  endAdornment: renderPasswordAdornment(showPassword, () => setShowPassword(!showPassword)),
                }}
              />

              <Typography
                sx={{
                  fontSize: { xs: "3.5vw", sm: "1.2vw", md: "0.97vw" },
                  color: "#000000",
                  mb: { xs: "2vw", md: "1.4%" },
                  ml: { xs: "2vw", md: "2%" },
                }}
              >
                Confirm Password
              </Typography>
              <StyledTextField
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                variant="outlined"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  endAdornment: renderPasswordAdornment(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword)),
                }}
              />
            </Box>

            <StyledButton
              variant="contained"
              onClick={handleReset}
              disabled={isLoading || isCooldown || !token}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Reset Password"
              )}
            </StyledButton>
          </>
        )}
      </Box>
    </StyledContainer>
  );
};

export default ResetPassword;
